/* eslint-disable @typescript-eslint/no-explicit-any */
import { deepCopy } from '../../helpers';

/**
 * Schema update from version 15 to version 16
 * - Restores corrupted svg string keys on override item components.
 *   A prior serialization bug wrote the resolved function object instead of the
 *   string key (e.g. 'gasolineCutout'). After JSON round-trip the functions were
 *   stripped, leaving svg as {} or another non-string value. This migration walks
 *   all items and restores the correct string key by SKU lookup or circular-cutout
 *   heuristic (width === height on a CUTOUT override).
 */

// SKU → svg key map built from component-item-data.json
const SKU_SVG_MAP: Record<string, string> = {
	'C1SINK1618': 'c1sink1618',
	'WOOD': 'logInsert',
	'MOK-VENT-SIDE': 'mokVentsSide',
	'MOK-VENT-BACK': 'mokVentsBack',
	'MOK-PASSTHROUGH': 'umbrellaCutout',
	'PASSTHROUGH-VENT': 'passthroughVent',
	'POP-UP-OUTLET': 'popUpOutlet',
	'DOUBLE-GANG-OUTLET': 'popUpOutlet',
	'UMBRELLA-CUTOUT': 'umbrellaCutout',
	'TOP_GAS_LINE_CUTOUT': 'umbrellaCutout',
	'GAS_LINE_CUTOUT': 'umbrellaCutout',
	'OUTLET-SINGLE-GANG': 'outletSingleGang',
	'OUTLET-SINGLE-GANG-HORIZONTAL': 'outletSingleGangHorizontal'
};

const isValidSvgKey = (svg: unknown): boolean =>
	typeof svg === 'string' || svg === null || svg === undefined;

const fixItemSvg = (item: any): void => {
	if (!item?.override || !item?.component) return;

	const { component } = item;
	if (isValidSvgKey(component.svg)) return;

	// Corrupted svg — restore from SKU map or circular cutout heuristic
	const sku = component.sku as string;
	if (SKU_SVG_MAP[sku]) {
		component.svg = SKU_SVG_MAP[sku];
		return;
	}

	// Custom circular cutouts: override with SKU 'CUTOUT' and equal width/height
	const { size } = component;
	if (sku === 'CUTOUT' && size && size.width === size.height) {
		component.svg = 'gasolineCutout';
		return;
	}

	// Unknown — clear to null so it doesn't break getSvgData()
	component.svg = null;
};

const fixIslandItems = (island: any): void => {
	const panels = island?.panels?.children;
	if (!panels?.length) return;

	panels.forEach((panel: any) => {
		const groups = panel?.panelItemGroups?.children;
		if (!groups?.length) return;

		groups.forEach((group: any) => {
			const items = group?.items?.children;
			if (!items?.length) return;

			items.forEach((panelItem: any) => {
				fixItemSvg(panelItem?.item);
			});
		});
	});
};

export const u16 = (blueprint: any): any => {
	console.log('schema-update:process-update:16:started');

	try {
		const updatedBlueprint = deepCopy(blueprint);
		updatedBlueprint.schemaVersion = 16;

		if (updatedBlueprint.kitchens?.children) {
			updatedBlueprint.kitchens.children.forEach((kitchen: any) => {
				const islands = kitchen.design?.islands?.children;
				if (islands?.length) {
					islands.forEach((island: any) => fixIslandItems(island));
				}

				const obstructions = kitchen.design?.obstructions?.children;
				if (obstructions?.length) {
					obstructions.forEach((obstruction: any) => fixIslandItems(obstruction));
				}
			});
		}

		console.log('schema-update:process-update:16:completed');
		return updatedBlueprint;
	} catch (err) {
		console.error('schema-update:process-update:16:error', err);
		return null;
	}
};
